import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';

import {
  ChatSurface,
  ChatSurfaceMessage,
  ChatSurfaceWidget,
} from '@/components/chat/chat-surface';
import { WidgetRenderer } from '@/components/chat/widgets';
import { ThemedText } from '@/components/themed-text';
import { useAssistantMessage, useAssistantThread } from '@/hooks/use-assistant';
import { isAgentUnavailable } from '@/hooks/use-onboarding-interview';
import { AssistantTurn, AssistantTurnRecord } from '@/types/assistant';
import { INTERVIEW_WIDGET } from '@/types/onboarding-interview';

interface Props {
  /** `503 AGENT_UNAVAILABLE`, inclusive no meio da conversa. */
  onAgentDown?: () => void;
  brandColor?: string;
}

/**
 * O assistente permanente (T-33): o mesmo `ChatSurface` do onboarding, agora
 * operando o negócio.
 *
 * Duas diferenças em relação à entrevista, e as duas são do produto:
 *
 * - **não abre pedindo nada.** A saudação vem do fio e não custa chamada ao
 *   modelo; quem começa o assunto é o prestador.
 * - **só o cartão do último turno responde.** Uma proposta de escrita que ficou
 *   para trás no fio já foi resolvida — deixá-la clicável seria oferecer
 *   confirmar o passado.
 */
export function AssistantChat({ onAgentDown, brandColor }: Props) {
  const thread = useAssistantThread();
  const turn = useAssistantMessage();

  const [pending, setPending] = useState<ChatSurfaceMessage[]>([]);
  const [failed, setFailed] = useState(false);
  /** O fio do servidor é a fonte da verdade; o local é só o que está em voo. */
  const lastSyncedAt = useRef<string | null>(null);

  const records = useMemo(() => thread.data?.messages ?? [], [thread.data]);
  const latestAt = records.at(-1)?.at ?? null;

  // Quando o servidor confirma o turno, as bolhas otimistas somem: elas já
  // estão no fio, e mantê-las duplicaria a fala na tela.
  useEffect(() => {
    if (latestAt && latestAt !== lastSyncedAt.current) {
      lastSyncedAt.current = latestAt;
      setPending([]);
    }
  }, [latestAt]);

  const send = useCallback(
    async (text: string) => {
      setFailed(false);
      setPending(prev => [
        ...prev,
        { id: `me-${prev.length}-${Date.now()}`, body: text, mine: true },
      ]);
      try {
        const reply: AssistantTurn = await turn.mutateAsync(text);
        setPending(prev => [
          ...prev,
          {
            id: `agent-${prev.length}-${Date.now()}`,
            body: reply.message,
            mine: false,
            widget: reply.widget,
          },
        ]);
        // O fio guardado é quem manda: recarregar traz as duas falas com o
        // cartão já ligado à proposta que o backend enfileirou.
        await thread.refetch();
      } catch (error) {
        if (isAgentUnavailable(error)) {
          onAgentDown?.();
          return;
        }
        setFailed(true);
      }
    },
    [onAgentDown, thread, turn],
  );

  // Memoizado porque a lista compara bolha por bolha para não redesenhar
  // cartão antigo: remontar os objetos a cada render anularia isso.
  const messages = useMemo<ChatSurfaceMessage[]>(
    () => [...records.map(toBubble), ...pending],
    [records, pending],
  );

  /**
   * Quem decide o "só o último cartão responde" é a superfície. Mas só o cartão
   * de confirmação **age**: os cartões de leitura (grade, serviços, perfil) são
   * espelho do dado e nunca devem ficar clicáveis — editá-los abriria o fluxo
   * do rascunho do onboarding, que não é desta tela.
   */
  const renderWidget = useCallback(
    (
      widget: ChatSurfaceWidget,
      _message: ChatSurfaceMessage,
      editable: boolean,
    ) => (
      <WidgetRenderer
        widget={widget}
        editable={editable && widget.type === INTERVIEW_WIDGET.CONFIRMATION}
      />
    ),
    [],
  );

  return (
    <ChatSurface
      messages={messages}
      onSend={text => void send(text)}
      sending={turn.isPending}
      isTyping={turn.isPending}
      isLoading={thread.isLoading}
      brandColor={brandColor}
      placeholder="Pergunte ou peça uma mudança"
      renderWidget={renderWidget}
      footer={
        failed ? (
          <View className="px-4 pb-1">
            <ThemedText className="text-center text-xs text-error-600">
              Não consegui responder agora. Tenta de novo.
            </ThemedText>
          </View>
        ) : null
      }
    />
  );
}

/** Uma fala guardada no fio vira bolha; o cartão vem junto. */
function toBubble(
  record: AssistantTurnRecord,
  index: number,
): ChatSurfaceMessage {
  return {
    id: `${record.role}-${index}-${record.at}`,
    body: record.text,
    mine: record.role === 'provider',
    widget: record.widget ?? null,
  };
}
